/* ═══════════════════════════════════════════════════════════════════════════
   Resolution scale adaptatif (PLAN.md §7.3, étage 2).

   Le resolution scale est le SEUL curseur touché en cours de partie, et ce
   n'est pas un hasard : il est continu, instantané, proportionnel au coût
   dominant — le fill rate — et surtout il NE RECOMPILE AUCUN SHADER. Changer
   de preset en jeu déclencherait de nouvelles variantes de matériaux, soit
   exactement le stutter qu'on cherche à éviter.

   Trois règles, chacune contre une erreur observable :

     1. On décide sur une MÉDIANE GLISSANTE, jamais sur la frame instantanée.
        Un ramasse-miettes de 40 ms déclencherait sinon une descente
        injustifiée, et le joueur verrait l'image se dégrader sans raison.

     2. L'hystérésis est ASYMÉTRIQUE : descente rapide (−10 %), remontée lente
        (+5 %, après 3 s de calme). Sans cela le jeu oscille visiblement entre
        net et flou toutes les quelques secondes — ce qui est PIRE que de
        rester bloqué en bas, parce que l'œil suit le changement.

     3. Les bornes sont dures. En dessous de 0,5 l'image devient une bouillie ;
        au-dessus de 1,0 on paierait du suréchantillonnage pour rien.

   Ce module est PUR : il ne connaît ni WebGL ni three, il prend des temps de
   frame et rend une échelle. C'est ce qui le rend testable sans GPU — et cette
   logique-là mérite d'être testée, parce que ses pannes sont lentes et
   difficiles à reproduire à la main.
   ═══════════════════════════════════════════════════════════════════════════ */

export const SCALE_MIN = 0.5;
export const SCALE_MAX = 1;

/** Au-dessus : on descend. En dessous : on peut remonter. L'écart entre les
 *  deux seuils est la zone morte qui empêche l'oscillation. */
export const DOWNSCALE_ABOVE_MS = 16;
export const UPSCALE_BELOW_MS = 12;

const WINDOW = 45;
const UPSCALE_COOLDOWN_SEC = 3;

export class QualityDirector {
  private readonly ring = new Float32Array(WINDOW);
  private readonly sorted = new Float32Array(WINDOW);
  private i = 0;
  private filled = 0;
  private cooldown = 0;
  private scale = SCALE_MAX;

  /** Nombre de descentes effectuées. Le plan en autorise 2 par run au maximum,
   *  et aucune remontée en cours de partie sur les presets — ici on suit
   *  seulement la statistique, la politique appartient à l'appelant. */
  downscales = 0;

  get value(): number {
    return this.scale;
  }

  /** `frameMs` doit être un temps de TRAVAIL, pas un intervalle de vsync.
   *  Alimenté avec des intervalles, ce régulateur descendrait en permanence sur
   *  tout écran 60 Hz — les intervalles y valent 16,7 ms même machine au repos. */
  sample(frameMs: number, dtSec: number): number {
    this.ring[this.i] = frameMs;
    this.i = (this.i + 1) % WINDOW;
    if (this.filled < WINDOW) this.filled++;

    if (this.cooldown > 0) this.cooldown -= dtSec;

    // On attend un échantillon complet : décider sur douze frames n'a pas de
    // sens et produirait des à-coups au démarrage, précisément quand les
    // shaders finissent de se compiler.
    if (this.filled < WINDOW) return this.scale;

    const med = this.median();

    if (med > DOWNSCALE_ABOVE_MS && this.scale > SCALE_MIN) {
      this.scale = Math.max(SCALE_MIN, this.scale - 0.1);
      this.downscales++;
      this.cooldown = UPSCALE_COOLDOWN_SEC;
      this.reset();
    } else if (med < UPSCALE_BELOW_MS && this.scale < SCALE_MAX && this.cooldown <= 0) {
      this.scale = Math.min(SCALE_MAX, this.scale + 0.05);
      this.cooldown = UPSCALE_COOLDOWN_SEC;
      this.reset();
    }

    return this.scale;
  }

  private median(): number {
    const s = this.sorted.subarray(0, this.filled);
    s.set(this.ring.subarray(0, this.filled));
    s.sort();
    return s[Math.floor(this.filled / 2)]!;
  }

  /** Vide la fenêtre après un changement : les mesures d'avant décrivaient une
   *  autre résolution et n'ont plus rien à dire sur la nouvelle. */
  private reset(): void {
    this.filled = 0;
    this.i = 0;
  }

  forceScale(v: number): void {
    this.scale = Math.min(SCALE_MAX, Math.max(SCALE_MIN, v));
    this.reset();
  }
}
